import { Fragment } from "react";
import { useData } from "@/context/DataContext";

/*
 * Resa del markdown di Discord per le anteprime: **grassetto**, *corsivo*, __sottolineato__, ~~barrato~~,
 * ||spoiler||, `codice`, blocchi ```, titoli #, -# testo piccolo, > citazioni, elenchi, [link](url),
 * menzioni <@id> <@&id> <#id>, emoji personalizzate <:nome:id>. Costruisce elementi React (niente HTML grezzo).
 */

const SAMPLE_USER = "Nuovo Utente";

// Valori di esempio per i segnaposto dei messaggi del bot.
export function useSampleVars() {
  const { guild } = useData() || {};
  const avatar = "https://cdn.discordapp.com/embed/avatars/0.png";
  return {
    user: "<@sample>", "user.id": "123456789012345678", "user.name": "nuovoutente", "user.displayName": SAMPLE_USER,
    "user.avatar": avatar, server: guild?.name || "Il mio server", "server.icon": guild?.icon || avatar,
    memberCount: String(guild?.memberCount ?? 128), boostCount: String(guild?.premiumSubscriptionCount ?? 7),
    title: "Titolo di esempio", url: "https://example.com", author: "NomeCreator", game: "Just Chatting",
    level: "5", xp: "1.250", date: new Date().toLocaleDateString("it-IT"),
  };
}

export const fillVars = (text, vars) =>
  typeof text === "string" ? text.replace(/\{([\w.]+)\}/g, (m, k) => (k in vars ? vars[k] : m)) : text;

const INLINE =
  /(`[^`\n]+`)|(\*\*\*[^*]+?\*\*\*)|(\*\*[\s\S]+?\*\*)|(__[\s\S]+?__)|(\*(?!\s)[^*\n]+?\*)|(_(?!\s)[^_\n]+?_)|(~~[\s\S]+?~~)|(\|\|[\s\S]+?\|\|)|(\[[^\]\n]+\]\(https?:\/\/[^)\s]+\))|(<@&\d+>)|(<@!?(?:\d+|sample)>)|(<#\d+>)|(<a?:\w+:\d+>)|(https?:\/\/[^\s<]+)|(\{[\w.]+\})/g;

const pill = "rounded-[3px] bg-[#5865F2]/30 px-0.5 font-medium text-[#c9cdfb]";

// Una funzione di resa per ogni gruppo della regex INLINE, nello stesso ordine.
const RULES = [
  (t, k) => <code key={k} className="rounded bg-[#1e1f22] px-1 py-0.5 font-mono text-[0.85em] text-[#DBDEE1]">{t.slice(1, -1)}</code>,
  (t, k) => <strong key={k}><em><Inline text={t.slice(3, -3)} /></em></strong>,
  (t, k) => <strong key={k} className="font-bold"><Inline text={t.slice(2, -2)} /></strong>,
  (t, k) => <u key={k}><Inline text={t.slice(2, -2)} /></u>,
  (t, k) => <em key={k}><Inline text={t.slice(1, -1)} /></em>,
  (t, k) => <em key={k}><Inline text={t.slice(1, -1)} /></em>,
  (t, k) => <s key={k}><Inline text={t.slice(2, -2)} /></s>,
  (t, k) => <span key={k} className="rounded bg-[#1e1f22] text-transparent transition-colors hover:bg-[#2b2d31] hover:text-inherit" title="Spoiler">{t.slice(2, -2)}</span>,
  (t, k) => {
    const [, label, href] = t.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    return <a key={k} href={href} target="_blank" rel="noreferrer" className="text-[#00A8FC] hover:underline"><Inline text={label} /></a>;
  },
  (t, k, ctx) => {
    const r = ctx.roles.find((x) => x.id === t.slice(3, -1));
    return <span key={k} className="rounded-[3px] px-0.5 font-medium" style={{ color: r?.color || "#c9cdfb", background: `${r?.color || "#5865F2"}26` }}>@{r?.name || "ruolo"}</span>;
  },
  (t, k) => <span key={k} className={pill}>@{t.includes("sample") ? SAMPLE_USER : "utente"}</span>,
  (t, k, ctx) => <span key={k} className={pill}>#{ctx.channels.find((x) => x.id === t.slice(2, -1))?.name || "canale"}</span>,
  (t, k) => {
    const [, anim, name, id] = t.match(/^<(a?):(\w+):(\d+)>$/);
    return <img key={k} src={`https://cdn.discordapp.com/emojis/${id}.${anim ? "gif" : "png"}`} alt={`:${name}:`} title={`:${name}:`} className="inline h-[1.375em] w-[1.375em] align-bottom" />;
  },
  (t, k) => <a key={k} href={t} target="_blank" rel="noreferrer" className="break-all text-[#00A8FC] hover:underline">{t}</a>,
  (t, k) => <span key={k} className="rounded bg-[#FEE75C]/15 px-0.5 font-mono text-[0.85em] text-[#FEE75C]" title="Segnaposto non riconosciuto">{t}</span>,
];

function Inline({ text }) {
  const data = useData() || {};
  const ctx = { roles: data.roles || [], channels: data.channels || [] };
  const out = [];
  let last = 0;
  let key = 0;
  for (const m of text.matchAll(INLINE)) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const group = m.findIndex((g, i) => i > 0 && g !== undefined);
    out.push(RULES[group - 1](m[0], key++, ctx));
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return <>{out}</>;
}

function Lines({ text }) {
  const lines = text.split("\n");
  const blocks = [];
  let quote = [];
  let list = [];
  const flush = () => {
    if (quote.length) { blocks.push(<div key={`q${blocks.length}`} className="my-0.5 flex gap-2"><span className="w-1 shrink-0 rounded bg-[#4E5058]" /><div><Lines text={quote.join("\n")} /></div></div>); quote = []; }
    if (list.length) { blocks.push(<ul key={`l${blocks.length}`} className="my-0.5 list-disc pl-5">{list.map((l, i) => <li key={i}><Inline text={l} /></li>)}</ul>); list = []; }
  };
  lines.forEach((line, i) => {
    const q = line.match(/^>\s?(.*)$/);
    const li = line.match(/^\s*[-*]\s+(.*)$/);
    if (q) { if (list.length) flush(); quote.push(q[1]); return; }
    if (li && !line.startsWith("-# ")) { if (quote.length) flush(); list.push(li[1]); return; }
    flush();
    const h = line.match(/^(#{1,3})\s+(.*)$/);
    const sub = line.match(/^-#\s+(.*)$/);
    if (h) {
      const size = ["text-[1.5em]", "text-[1.25em]", "text-[1.1em]"][h[1].length - 1];
      blocks.push(<div key={i} className={`${size} my-1 font-bold leading-tight text-white`}><Inline text={h[2]} /></div>);
    } else if (sub) blocks.push(<div key={i} className="text-[0.8em] text-[#949BA4]"><Inline text={sub[1]} /></div>);
    else blocks.push(<Fragment key={i}>{line === "" ? <br /> : <div><Inline text={line} /></div>}</Fragment>);
  });
  flush();
  return <>{blocks}</>;
}

export function DiscordMarkdown({ text, className = "" }) {
  if (!text) return null;
  const parts = String(text).split(/```(?:\w*\n)?([\s\S]*?)```/g);
  return (
    <div className={`break-words ${className}`}>
      {parts.map((p, i) =>
        i % 2 === 1
          ? <pre key={i} className="my-1 overflow-x-auto rounded border border-[#1e1f22] bg-[#2b2d31] p-2 font-mono text-[0.85em] text-[#DBDEE1]">{p}</pre>
          : <Lines key={i} text={p} />,
      )}
    </div>
  );
}
